import React from 'react';
import { Card, Empty, Typography } from 'antd';

const { Text } = Typography;

/**
 * P1 M&V contract, frontend side.
 *
 * The backend returns `{ status, reason, metrics, fieldReasons, provenance }`.
 * These helpers ensure the UI renders absent data as absent — never as a
 * default number (the old `|| 150`, `|| 6.7`, fabricated fallback blocks).
 */

export interface SavingsPayload {
  status?: 'ok' | 'insufficient_data';
  reason?: string | null;
  metrics?: Record<string, number | null> | null;
  fieldReasons?: Record<string, string>;
  provenance?: Record<string, unknown>;
}

export const SAVINGS_REASON_TEXT: Record<string, string> = {
  no_consumption_data_in_period:
    'No metered consumption data has been recorded for this period yet.',
  generation_data_only_no_metered_consumption:
    'Generation telemetry exists, but no consumption meter feeds savings figures yet.',
  no_baseline_data_in_period:
    'There is not yet a prior metered period to fit a baseline against.',
  insufficient_coverage:
    'Recorded data does not cover enough of the period to produce trustworthy figures.',
  no_location_recorded:
    'No location is recorded, so dollar figures cannot use a real tariff.',
};

/** Returns metrics only when the backend vouched for them (status === 'ok'). */
export const getSavingsMetrics = (
  payload: any,
): Record<string, number | null> | null =>
  payload && payload.status === 'ok' && payload.metrics ? payload.metrics : null;

export const savingsReasonText = (payload: any): string =>
  (payload && payload.reason && SAVINGS_REASON_TEXT[payload.reason]) ||
  'Metered data is not available for this period yet.';

interface NoMeteredDataCardProps {
  payload?: any;
  title?: string;
  detail?: string;
}

/**
 * Honest empty state: explains WHY there is no number instead of
 * showing a fabricated one.
 */
export const NoMeteredDataCard: React.FC<NoMeteredDataCardProps> = ({
  payload,
  title = 'No verified figures yet',
  detail,
}) => (
  <Card>
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div>
          <strong>{title}</strong>
          <div style={{ marginTop: 8 }}>{detail || savingsReasonText(payload)}</div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
            Figures appear only when real metered records exist — nothing here is estimated.
          </Text>
        </div>
      }
    />
  </Card>
);